import React, { useEffect, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Label from '../../../../../Label';
import Button from '../../../../../Button';
import ImageUpload from '../../../../../ImageUpload';
import useDebounce from '../../../../../../../hooks/useDebounce';
import useAssetImageUpload from '../../../../../../../hooks/useAssetImageUpload';
import { IMAGE_SIZE_CODE } from '../../../../../../../constants/image-size-codes';
import useAccountPrintAssets from '../../../../../../../apps/pro/views/Account/AccountPrintAssets/components/AccountPrintAsset/hooks/useAccountPrintAssets';
import { PRINT_ASSET_TYPES } from '../../../../../../../constants/print-assets';
import FormGroup from '../../../../../FormGroup';
import RangeSlider from '../../../../../RangeSlider';
import Input from '../../../../../Input';
import { PHOTO_TYPES } from '../../../../../../../constants/photo-types';

const propTypes = {
  className: PropTypes.string,
  element: PropTypes.shape({
    cardElement: PropTypes.object.isRequired,
    cardElementConfiguration: PropTypes.object.isRequired,
  }).isRequired,
  onElementConfigurationChange: PropTypes.func.isRequired,
  zElementIndexMax: PropTypes.number.isRequired,
};

const CardDesignerForegroundControls = ({
  className,
  element,
  onCardElementCreate,
  onElementConfigurationChange,
  zElementIndexMax,
}) => {
  const [opacity, setOpacity] = useState(1);
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebounce(query, 500);

  const { printAssets, printAssetsTotal, setSearchQuery } =
    useAccountPrintAssets({
      printAssetType: PRINT_ASSET_TYPES.FOREGROUND,
    });

  const { cardElementConfiguration } = element ?? {};
  const { opacity: elementOpacity } = cardElementConfiguration ?? {};

  useEffect(() => {
    setSearchQuery(debouncedQuery);
  }, [debouncedQuery]);

  useEffect(() => {
    setOpacity(elementOpacity ?? 1);
  }, [elementOpacity]);

  const classes = classNames({
    [className]: !!className,
  });

  const { uploading, onUpload, onUploadReset } = useAssetImageUpload({
    onImageUploaded: async assetImage => {
      if (element) {
        onElementConfigurationChange({
          element,
          elementConfig: {
            asset_image_id: assetImage.id,
          },
        });
      } else {
        onCardElementCreate({
          cardElementType: 'PHOTO',
          cardElementConfiguration: {
            is_editable: false,
            asset_image_id: assetImage.id,
            photo_type: PHOTO_TYPES.FOREGROUND,
          },
          input: {
            frame: {
              x: 0,
              y: 0,
              width: 1,
              height: 1,
            },
            z_index: zElementIndexMax,
          },
        });
      }
    },
  });

  return (
    <div className={classes}>
      <Label
        id="foreground-control"
        labelTip="Foregrounds are used for graphic elements only. Please upload images containing athletes or students to a photo box and use the Layering feature to adjust to your preferred placement."
      >
        Foreground Assets
      </Label>
      <ImageUpload
        id="card-designer-foreground-image"
        buttonText="Upload an image"
        loading={uploading}
        disabled={uploading}
        customFileInputAccept="image/x-png, image/png, .png"
        image={
          cardElementConfiguration?.asset_image
            ? {
                id: cardElementConfiguration.asset_image.id,
                thumbnail_urls: Object.values(IMAGE_SIZE_CODE).map(size => ({
                  size_code: size,
                  url: cardElementConfiguration.asset_image.url,
                  width: 0,
                  height: 0,
                })),
              }
            : null
        }
        onInputChange={event => {
          const [file] = event.target.files;
          onUpload(file);
        }}
        onImageDismiss={() => {
          onUploadReset();
          onElementConfigurationChange({
            element,
            elementConfig: {
              asset_image_id: null,
            },
          });
        }}
      />
      {cardElementConfiguration?.asset_image && (
        <div className="card-designer-foreground-opacity-container">
          <span className="mr-1">Opacity</span>
          <RangeSlider
            className="mx-2"
            onChange={values => {
              setOpacity(Math.round((values[0] / 1e2) * 1e2) / 1e2);
            }}
            onFinalChange={values => {
              onElementConfigurationChange({
                element,
                elementConfig: {
                  opacity: Math.round((values[0] / 1e2) * 1e2) / 1e2,
                },
              });
            }}
            values={[Math.round(opacity * 1e2)]}
            min={0}
            max={100}
          />
          <Input
            id="opacity"
            type="number"
            max={100}
            min={0}
            className="mr-1"
            value={Math.round(opacity * 1e2)}
            onChange={({ target }) => {
              const percentValue = Math.round((target.value / 1e2) * 1e2) / 1e2;
              setOpacity(percentValue);
              onElementConfigurationChange({
                element,
                elementConfig: {
                  opacity: percentValue,
                },
              });
            }}
          />{' '}
          <span className="input-addon-text">%</span>
        </div>
      )}
      {printAssetsTotal > 0 && (
        <Fragment>
          <FormGroup
            id="query"
            name="query"
            label="Query"
            labelSrOnly
            className="mt-2"
            placeholder="Search your foregrounds.."
            onChange={({ target }) => setQuery(target.value)}
            value={query}
          />

          <div className="card-designer-print-assets-container">
            {printAssets.map(printAsset => (
              <Button
                className="card-designer-print-assets-item"
                btnStyle="reset"
                onClick={() => {
                  if (element) {
                    onElementConfigurationChange({
                      element,
                      elementConfig: {
                        existing_asset_image_id: printAsset.asset_image.id,
                      },
                    });
                  } else {
                    onCardElementCreate({
                      cardElementType: 'PHOTO',
                      cardElementConfiguration: {
                        is_editable: false,
                        existing_asset_image_id: printAsset.asset_image.id,
                        photo_type: PHOTO_TYPES.FOREGROUND,
                      },
                      input: {
                        frame: {
                          x: 0,
                          y: 0,
                          width: 1,
                          height: 1,
                        },
                        z_index: zElementIndexMax,
                      },
                    });
                  }
                }}
              >
                <img
                  src={printAsset.asset_image.url}
                  alt={printAsset.name}
                  className="card-designer-print-backgrounds-assets-image"
                />
              </Button>
            ))}
          </div>
        </Fragment>
      )}
    </div>
  );
};

CardDesignerForegroundControls.propTypes = propTypes;

export default CardDesignerForegroundControls;
